import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { COLUMNAS, CLAVES, FILA_EJEMPLO, parsearFecha, aSerieExcel, FORMATO_FECHA } from './excelConvenios';

// El OOXML se arma a mano porque el build comunitario de SheetJS no escribe ni
// validacion de datos (los desplegables) ni relleno de celda (el color), que es
// justo lo que pide la plantilla. Todo se deriva de COLUMNAS.

const NOMBRE_ARCHIVO = 'Plantilla carga masiva de convenios.xlsx';
const NOMBRE_HOJA = 'Convenios';
const NOMBRE_HOJA_CATALOGOS = '_catalogos';
const RANGO_PAISES = 'LISTA_PAISES';
const ULTIMA_FILA = 1000;          // hasta donde llegan las validaciones
const ROJO = 'FF900807';           // mismo primario de la plataforma
const ROJO_CLARO = 'FFB70E0C';
const GRIS_BORDE = 'FFD9D9D9';

const esc = (v) => String(v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;');

// 0 -> A, 25 -> Z, 26 -> AA
const letraColumna = (i) => {
  let s = '';
  for (let n = i; n >= 0; n = Math.floor(n / 26) - 1) s = String.fromCharCode(65 + (n % 26)) + s;
  return s;
};

const indiceDe = (clave) => CLAVES.indexOf(clave);
const columnaDe = (clave) => letraColumna(indiceDe(clave));

// Indices de cellXfs, en el mismo orden en que se declaran abajo.
const XF_ENCABEZADO = 1;
const XF_FECHA = 2;
const XF_TEXTO = 3;
const XF_ENTERO = 4;

// Excel exige que los dos primeros rellenos sean 'none' y 'gray125'.
const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="dd/mm/yyyy"/></numFmts>
<fonts count="2">
<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="3">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="${ROJO}"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="3">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border><left style="thin"><color rgb="${ROJO_CLARO}"/></left><right style="thin"><color rgb="${ROJO_CLARO}"/></right><top style="thin"><color rgb="${ROJO_CLARO}"/></top><bottom style="medium"><color rgb="${ROJO_CLARO}"/></bottom><diagonal/></border>
<border><left style="thin"><color rgb="${GRIS_BORDE}"/></left><right style="thin"><color rgb="${GRIS_BORDE}"/></right><top style="thin"><color rgb="${GRIS_BORDE}"/></top><bottom style="thin"><color rgb="${GRIS_BORDE}"/></bottom><diagonal/></border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="5">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="164" fontId="0" fillId="0" borderId="2" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
<xf numFmtId="49" fontId="0" fillId="0" borderId="2" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment vertical="center"/></xf>
<xf numFmtId="1" fontId="0" fillId="0" borderId="2" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
<dxfs count="0"/>
<tableStyles count="0" defaultTableStyle="TableStyleMedium2"/>
</styleSheet>`;

const celdaTexto = (ref, valor, estilo) =>
  `<c r="${ref}" s="${estilo}" t="inlineStr"><is><t xml:space="preserve">${esc(valor)}</t></is></c>`;

const celdaNumero = (ref, valor, estilo) =>
  `<c r="${ref}" s="${estilo}"><v>${valor}</v></c>`;

// Lista en la propia formula: limite de 255 caracteres y ninguna opcion con coma.
const validacionLista = (col, opciones) => {
  let rango = `${col}2:${col}${ULTIMA_FILA}`;
  return `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1"`
    + ` errorStyle="stop" errorTitle="Valor no válido"`
    + ` error="Seleccione una de las opciones de la lista." sqref="${rango}">`
    + `<formula1>&quot;${esc(opciones.join(','))}&quot;</formula1></dataValidation>`;
};

// El formato con barras lo impone el numFmt de la celda, no esta validacion.
const validacionFecha = (col) => {
  let rango = `${col}2:${col}${ULTIMA_FILA}`;
  return `<dataValidation type="date" operator="between" allowBlank="1" showInputMessage="1" showErrorMessage="1"`
    + ` errorStyle="stop" errorTitle="Fecha no válida"`
    + ` error="Escriba una fecha con el formato ${FORMATO_FECHA}, por ejemplo 12/12/2000."`
    + ` promptTitle="Fecha" prompt="Formato ${FORMATO_FECHA}, por ejemplo 12/12/2000." sqref="${rango}">`
    + `<formula1>1</formula1><formula2>2958465</formula2></dataValidation>`;
};

// Los 247 paises no caben en una formula en linea: van en una hoja oculta.
const validacionRango = (col, rango) => {
  let celdas = `${col}2:${col}${ULTIMA_FILA}`;
  return `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1"`
    + ` errorStyle="stop" errorTitle="Valor no válido"`
    + ` error="Seleccione uno de los valores de la lista." sqref="${celdas}">`
    + `<formula1>${rango}</formula1></dataValidation>`;
};

const validaciones = () => {
  let reglas = [];
  // La vigencia no lleva validacion: es un entero sin rango, y la celda ya va con
  // formato numerico.
  COLUMNAS.forEach(({ clave, tipo, opciones }) => {
    let col = columnaDe(clave);
    if (tipo === 'rango') reglas.push(validacionRango(col, RANGO_PAISES));
    else if (tipo === 'lista') reglas.push(validacionLista(col, opciones));
    else if (tipo === 'fecha') reglas.push(validacionFecha(col));
  });
  return `<dataValidations count="${reglas.length}">${reglas.join('')}</dataValidations>`;
};

const filaEncabezados = () => {
  let celdas = COLUMNAS.map(({ clave }, i) => celdaTexto(letraColumna(i) + '1', clave, XF_ENCABEZADO));
  return `<row r="1" ht="30" customHeight="1">${celdas.join('')}</row>`;
};

const filaEjemplo = () => {
  let celdas = COLUMNAS.map(({ clave, tipo }, i) => {
    let ref = letraColumna(i) + '2';
    let valor = FILA_EJEMPLO[clave];
    if (tipo === 'fecha') {
      let d = parsearFecha(valor);
      return d ? celdaNumero(ref, aSerieExcel(d), XF_FECHA) : celdaTexto(ref, valor, XF_FECHA);
    }
    if (tipo === 'entero') return celdaNumero(ref, Number(valor), XF_ENTERO);
    return celdaTexto(ref, valor, XF_TEXTO);
  });
  return `<row r="2" ht="18" customHeight="1">${celdas.join('')}</row>`;
};

// El orden lo impone el esquema: cols, sheetData, autoFilter, dataValidations,
// pageMargins. Cambiarlo hace que Excel declare el archivo dañado.
const hojaXml = () => {
  let ultima = letraColumna(COLUMNAS.length - 1);
  let cols = COLUMNAS.map(({ ancho }, i) =>
    `<col min="${i + 1}" max="${i + 1}" width="${ancho}" customWidth="1"/>`).join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<dimension ref="A1:${ultima}2"/>
<sheetViews><sheetView tabSelected="1" workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft" activeCell="A2" sqref="A2"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
<cols>${cols}</cols>
<sheetData>${filaEncabezados()}${filaEjemplo()}</sheetData>
<autoFilter ref="A1:${ultima}1"/>
${validaciones()}
<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>
</worksheet>`;
};

const hojaCatalogosXml = (paises) => {
  let filas = paises.map((nombre, i) =>
    `<row r="${i + 1}">${celdaTexto('A' + (i + 1), nombre, XF_TEXTO)}</row>`).join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<dimension ref="A1:A${Math.max(1, paises.length)}"/>
<cols><col min="1" max="1" width="40" customWidth="1"/></cols>
<sheetData>${filas}</sheetData>
</worksheet>`;
};

const CONTENT_TYPES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/worksheets/sheet2.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const workbookXml = (paises) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>
<sheet name="${NOMBRE_HOJA}" sheetId="1" r:id="rId1"/>
<sheet name="${NOMBRE_HOJA_CATALOGOS}" sheetId="2" state="hidden" r:id="rId2"/>
</sheets>
<definedNames><definedName name="${RANGO_PAISES}">${NOMBRE_HOJA_CATALOGOS}!$A$1:$A$${Math.max(1, paises.length)}</definedName></definedNames>
</workbook>`;

const WORKBOOK_RELS_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet2.xml"/>
<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

// `tipo`: 'blob' en el navegador, 'nodebuffer' en las pruebas.
export const construirPlantilla = (paises = [], tipo = 'blob') => {
  let lista = (paises || []).filter(Boolean);
  let zip = new JSZip();
  zip.file('[Content_Types].xml', CONTENT_TYPES_XML);
  zip.file('_rels/.rels', RELS_XML);
  zip.file('xl/workbook.xml', workbookXml(lista));
  zip.file('xl/_rels/workbook.xml.rels', WORKBOOK_RELS_XML);
  zip.file('xl/styles.xml', STYLES_XML);
  zip.file('xl/worksheets/sheet1.xml', hojaXml());
  zip.file('xl/worksheets/sheet2.xml', hojaCatalogosXml(lista));
  return zip.generateAsync({
    type: tipo,
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    compression: 'DEFLATE',
  });
};

export const generarPlantilla = (paises = []) => construirPlantilla(paises, 'blob')
  .then((blob) => saveAs(blob, NOMBRE_ARCHIVO));

export default generarPlantilla;
